import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Briefcase, MapPin, Calendar, ChevronRight } from 'lucide-react';
import { portfolioData } from '../data/mock';

const Experience = () => {
  const { experience } = portfolioData;
  const [expandedId, setExpandedId] = useState(experience[0]?.id);

  return (
    <section id="experience" className="py-20 bg-slate-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-20 right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Professional <span className="text-blue-400">Experience</span>
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            11+ years of driving quality excellence and leading high-performing engineering teams
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-blue-600 to-transparent"></div>

            {experience.map((exp, index) => (
              <div
                key={exp.id}
                className={`relative mb-8 md:mb-12 ${
                  index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2 md:ml-auto'
                }`}
              >
                {/* Timeline Dot */}
                <div className="hidden md:block absolute left-1/2 top-8 w-4 h-4 bg-blue-500 rounded-full border-4 border-slate-950 -translate-x-1/2 shadow-lg shadow-blue-500/50"></div>

                <Card
                  className={`bg-slate-900/50 border-slate-700 hover:border-blue-500/50 transition-all duration-300 p-6 ${
                    index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                  }`}
                >
                  {/* Company Header */}
                  <div className="mb-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 flex items-center gap-2">
                          <Briefcase className="text-blue-400 flex-shrink-0" size={24} />
                          {exp.company}
                        </h3>
                        <p className="text-blue-400 font-semibold text-base sm:text-lg mb-2">{exp.role}</p>
                      </div>
                      {exp.type === 'leadership' && (
                        <Badge className="bg-blue-600/20 border-blue-500/30 text-blue-300 text-xs flex-shrink-0 ml-2">
                          Leadership
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={16} className="flex-shrink-0" />
                        {exp.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={16} className="flex-shrink-0" />
                        {exp.location}
                      </span>
                    </div>

                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed text-left">{exp.description}</p>
                  </div>

                  {/* Achievements Toggle */}
                  <button
                    onClick={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium mb-3"
                  >
                    <ChevronRight
                      size={16}
                      className={`transform transition-transform duration-300 ${
                        expandedId === exp.id ? 'rotate-90' : ''
                      }`}
                    />
                    {expandedId === exp.id ? 'Hide' : 'Show'} Key Achievements
                  </button>

                  {/* Achievements List */}
                  {expandedId === exp.id && (
                    <div className="space-y-2 mb-4 animate-fade-in">
                      {exp.achievements.map((achievement, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                          <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2"></div>
                          <span>{achievement}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.slice(0, 6).map((tech, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="border-slate-600 text-gray-400 text-xs hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                      >
                        {tech}
                      </Badge>
                    ))}
                    {exp.technologies.length > 6 && (
                      <Badge variant="outline" className="border-slate-600 text-gray-400 text-xs">
                        +{exp.technologies.length - 6} more
                      </Badge>
                    )}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

export default Experience;